import { useState, useCallback, useEffect } from "react";
import { useFormContext } from "../context/FormContext";

export const useFormBuilder = (formId) => {
  const { updateForm, getForm } = useFormContext();
  const [formFields, setFormFields] = useState([]);
  const [formTitle, setFormTitle] = useState("Yeni Form");
  const [formDescription, setFormDescription] = useState("");
  const [formSettings, setFormSettings] = useState({
    // Görünüm Ayarları
    backgroundColor: "#ffffff",
    titleColor: "#000000",
    descriptionColor: "#666666",
    submitButtonColor: "#1976d2",
    submitButtonTextColor: "#ffffff",
    fieldSize: "medium",
    formWidth: "medium",
    submitButtonText: "Gönder",
    fontFamily: "Roboto",
    fontSize: "medium",
    fieldBorderRadius: "4px",
    fieldBorderColor: "#e0e0e0",
    fieldBackgroundColor: "#ffffff",
    fieldFocusColor: "#1976d2",

    // Doküman Kontrol Sistemi
    documentControlEnabled: false,
    documentPrefix: "DOK",
    documentCode: "001",
    department: "",

    // Animasyon Ayarları
    animationEnabled: false,
    animationType: "fade",

    // Erişilebilirlik Ayarları
    accessibilityEnabled: false,
    highContrastMode: false,

    // Güvenlik Ayarları
    securityEnabled: false,
    captchaEnabled: false,
    maxSubmissions: 1000,

    // E-posta Bildirimleri
    enableEmailNotifications: false,
    notificationEmail: "",
    requireCaptcha: false,
    limitSubmissions: false,
    maxSubmissions: 100,

    loginRequired: true,
  });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (formId) {
      const form = getForm(formId);
      if (form) {
        setFormFields(form.fields || []);
        setFormTitle(form.title || "Yeni Form");
        setFormDescription(form.description || "");
        setFormSettings(form.settings || {});
      }
    }
  }, [formId, getForm]);

  const getDefaultLabel = (fieldType) => {
    switch (fieldType) {
      case "text":
        return "Kısa Metin Alanı";
      case "textarea":
        return "Uzun Metin Alanı";
      case "email":
        return "E-posta Alanı";
      case "phone":
        return "Telefon Alanı";
      case "number":
        return "Sayı Alanı";
      case "select":
        return "Açılır Liste";
      case "checkbox":
        return "Onay Kutusu";
      case "radio":
        return "Seçenek Düğmeleri";
      case "date":
        return "Tarih Alanı";
      case "time":
        return "Saat Alanı";
      case "file":
        return "Dosya Yükleme";
      case "image":
        return "Görsel Yükleme";
      case "pdf":
        return "PDF Yükleme";
      case "pdfDocument":
        return "PDF Doküman Eki";
      case "name":
        return "Ad Soyad Alanı";
      case "signature":
        return "İmza Alanı";
      case "table":
        return "Tablo Alanı";
      default:
        return "Yeni Alan";
    }
  };

  const getDefaultPlaceholder = (fieldType) => {
    switch (fieldType) {
      case "text":
        return "Metin giriniz";
      case "textarea":
        return "Açıklama giriniz";
      case "email":
        return "E-posta adresinizi giriniz";
      case "phone":
        return "Telefon numaranızı giriniz";
      case "number":
        return "Sayı giriniz";
      case "select":
        return "Seçim yapınız";
      case "date":
        return "Tarih seçiniz";
      case "time":
        return "Saat seçiniz";
      case "name":
        return "Ad Soyad giriniz";
      default:
        return "";
    }
  };

  const handleAddField = (fieldType, fieldData = null, page = 1) => {
    console.log("🔧 handleAddField çağrıldı:", { fieldType, fieldData, page });

    // Excel import verisi varsa onu kullan, yoksa default değerler
    const baseField = fieldData || {};

    const newField = {
      id: baseField.id || Date.now().toString(),
      type: fieldType,
      label: baseField.label || getDefaultLabel(fieldType),
      required: baseField.required || false,
      placeholder: baseField.placeholder || getDefaultPlaceholder(fieldType),
      helpText: baseField.helpText || "",
      size: baseField.size || "12",
      page: baseField.page || page || 1, // Page defaultu 1 olsun
    };

    console.log("🔧 Yeni alan oluşturuluyor:", newField);

    // Eğer fieldData'dan geliyorsa (Excel import) ek özellikleri koru
    if (fieldData && typeof fieldData === "object" && fieldData !== null) {
      Object.keys(fieldData).forEach((key) => {
        if (!newField.hasOwnProperty(key)) {
          newField[key] = fieldData[key];
        }
      });
    }

    // Alan tipine göre özel ayarlar (sadece fieldData yoksa uygula)
    if (!fieldData || typeof fieldData !== "object") {
      switch (fieldType) {
        case "select":
        case "checkbox":
        case "radio":
          newField.options = ["1. Seçenek", "2. Seçenek", "3. Seçenek"];
          break;
        case "file":
          newField.accept = "*/*";
          newField.multiple = false;
          newField.maxSize = 5242880; // 5MB
          break;
        case "image":
          newField.accept = "image/*";
          newField.multiple = true;
          newField.maxSize = 5242880; // 5MB
          newField.maxFiles = 5;
          break;
        case "pdf":
          newField.accept = ".pdf";
          newField.multiple = false;
          newField.maxSize = 20971520; // 20MB
          newField.maxFiles = 1;
          newField.uploadText =
            "PDF dosyası yüklemek için tıklayın veya sürükleyin";
          newField.uploadSubText = "Desteklenen format: PDF (max 20MB)";
          newField.allowMultiple = false;
          newField.maxFileSize = 20971520; // 20MB
          newField.uploadedFiles = [];
          break;
        case "pdfDocument":
          newField.accept = ".pdf";
          newField.multiple = false;
          newField.maxSize = 20971520; // 20MB
          newField.maxFiles = 1;
          newField.uploadText =
            "Formla birlikte gösterilecek PDF dosyasını yükleyin";
          newField.uploadSubText =
            "Bu PDF, formu dolduran kişiler tarafından görüntülenebilir (max 20MB)";
          newField.allowMultiple = false;
          newField.maxFileSize = 20971520; // 20MB
          newField.uploadedFiles = [];
          newField.isReference = true; // Bu bir referans doküman
          newField.showToUsers = true; // Kullanıcılara gösterilecek
          break;

        case "table":
          newField.columns = [
            {
              header: "Sütun 1",
              type: "text",
              placeholder: "Metin giriniz",
              width: "auto",
              options: [], // select tipi için kullanılacak
            },
            {
              header: "Sütun 2",
              type: "text",
              placeholder: "Metin giriniz",
              width: "auto",
              options: [],
            },
          ];
          newField.minRows = 1;
          newField.maxRows = 10;
          newField.showRowCount = true;
          newField.defaultRows = [
            Array(2).fill(""), // 2 sütun için boş değerler
          ];
          newField.validation = {
            required: false,
            uniqueColumns: [], // benzersiz olması gereken sütunlar
            customValidation: {}, // özel doğrulama kuralları
          };
          break;
        default:
          break;
      }
    }

    console.log("🔧 Son alan nesnesi:", newField);
    setFormFields((prevFields) => {
      const newFields = [...prevFields, newField];
      console.log("🔧 Yeni formFields listesi:", newFields);
      return newFields;
    });
    setIsDirty(true);
  };

  const handleRemoveField = useCallback((fieldId) => {
    setFormFields((prev) => prev.filter((field) => field.id !== fieldId));
    setIsDirty(true);
  }, []);

  const handleUpdateField = useCallback((fieldId, updates) => {
    setFormFields((prev) =>
      prev.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      )
    );
    setIsDirty(true);
  }, []);

  const handleDuplicateField = useCallback((field) => {
    const newField = {
      ...field,
      id: Date.now(),
      label: `${field.label} (Kopya)`,
    };
    setFormFields((prev) => [...prev, newField]);
    setIsDirty(true);
  }, []);

  const handleReorderFields = useCallback((newFields) => {
    setFormFields(newFields);
    setIsDirty(true);
  }, []);

  const handleUpdateSettings = useCallback((newSettings) => {
    console.log("🔧 handleUpdateSettings çağrıldı:", newSettings);
    setFormSettings((prevSettings) => {
      const updatedSettings = {
        ...prevSettings,
        ...newSettings,
      };
      console.log("🔧 Güncellenmiş ayarlar:", updatedSettings);
      return updatedSettings;
    });
    setIsDirty(true);
  }, []);

  const handleUpdateFormDetails = useCallback((key, value) => {
    if (key === "title") {
      setFormTitle(value);
    } else if (key === "description") {
      setFormDescription(value);
    }
    setIsDirty(true);
  }, []);

  const handleSaveForm = useCallback(async () => {
    try {
      const formData = {
        id: formId || Date.now(),
        title: formTitle,
        description: formDescription,
        fields: formFields,
        settings: formSettings,
        updatedAt: new Date().toISOString(),
      };

      console.log("=========== formdata", formData);

      await updateForm(formData);
      setIsDirty(false);
      return formData;
    } catch (error) {
      console.error("Form kaydedilirken hata oluştu:", error);
      throw error;
    }
  }, [
    formId,
    formTitle,
    formDescription,
    formFields,
    formSettings,
    updateForm,
  ]);

  return {
    formFields,
    formSettings,
    formTitle,
    formDescription,
    isDirty,
    handleAddField,
    handleRemoveField,
    handleUpdateField,
    handleDuplicateField,
    handleReorderFields,
    handleUpdateSettings,
    handleUpdateFormDetails,
    handleSaveForm,
    setIsDirty,
  };
};
